import { T } from '@tolgee/react';
import { Dropdown, Menu, Space, Tooltip } from 'antd';
import { useState } from 'react';
import { LuCamera, LuClipboardPaste, LuPaperclip, LuYoutube } from 'react-icons/lu';
import { RiSlideshow2Line } from 'react-icons/ri';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../../assets/icons';
import theme from '../../../../assets/theme';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useMetrics } from '../../../../hooks/useMetrics';
import { MaterialAttachment } from '../../../../hooks/useUploadMaterialAttachment';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { UseMyTextModal } from '../UseMyTextModal';

export function MaterialsMenu({
  tenantId,
  onClickUploadFile,
  onFileUpload,
  eventMetadata,
  onSelectCurifyMySlides,
}: {
  tenantId: string;
  onClickUploadFile: () => void;
  onFileUpload: (file: MaterialAttachment) => void;
  eventMetadata: Record<string, unknown>;
  onSelectCurifyMySlides: () => void;
}) {
  const metrics = useMetrics();
  const [showPasteModal, setShowPasteModal] = useState(false);
  const isDesktop = useBreakPoint(700);
  const isAddMaterialFileEnabled = useFeatureFlag('add-material-file');

  const isPersonalizedHomePageEnabled = useFeatureFlag('personalized_create_page');

  return (
    <>
      <Dropdown
        overlay={
          <Menu>
            <StyledMenuItem
              key="text-paste"
              onClick={() => {
                metrics.logEvent(
                  'CreatePage.UseMyMaterials.SelectPasteInTextOption',
                  eventMetadata,
                );
                setShowPasteModal(true);
              }}
            >
              <IconWrapper height="16px" width="16px">
                {/* @ts-expect-error react-icons type mismatch */}
                <LuClipboardPaste />
              </IconWrapper>
              <span>
                <T
                  keyName="UseMyMaterials.Text.PasteOption"
                  defaultValue="Paste in text"
                />
              </span>
            </StyledMenuItem>

            <StyledMenuItem
              key="text-file-upload"
              disabled={!isAddMaterialFileEnabled}
              onClick={() => {
                metrics.logEvent(
                  'CreatePage.UseMyMaterials.SelectUploadFileOption',
                  eventMetadata,
                );
                onClickUploadFile();
              }}
            >
              <IconWrapper height="16px" width="16px">
                {/* @ts-expect-error react-icons type mismatch */}
                <LuPaperclip />
              </IconWrapper>
              <span>
                <T
                  keyName="UseMyMaterials.UploadFileOption"
                  defaultValue="Upload a file"
                />
              </span>
            </StyledMenuItem>
            <StyledMenuItem
              key="text-file-pic-upload"
              disabled={!isAddMaterialFileEnabled}
              onClick={() => {
                metrics.logEvent(
                  'CreatePage.UseMyMaterials.SelectUploadFileImageOption',
                  eventMetadata,
                );
                onClickUploadFile();
              }}
            >
              <IconWrapper height="16px" width="16px">
                {/* @ts-expect-error react-icons type mismatch */}
                <LuCamera />
              </IconWrapper>
              <span>
                <T
                  keyName="UseMyMaterials.UploadFileImageOption"
                  defaultValue="Upload an image or screenshot"
                />
              </span>
            </StyledMenuItem>
            {!isPersonalizedHomePageEnabled && (
              <StyledMenuItem
                key="presentation"
                onClick={() => {
                  metrics.logEvent(
                    'CreatePage.UseMyMaterials.SelectUploadSlidesOption',
                    eventMetadata,
                  );
                  onSelectCurifyMySlides();
                }}
              >
                <IconWrapper height="16px" width="16px">
                  {/* @ts-expect-error react-icons type mismatch */}
                  <RiSlideshow2Line />
                </IconWrapper>
                <span>
                  <T
                    keyName="ImportMaterials.button.slides"
                    defaultValue="Upload slides"
                  />
                </span>
              </StyledMenuItem>
            )}
            <Tooltip
              title={<T keyName="Coming Soon" defaultValue="Coming Soon" />}
              placement="right"
            >
              <StyledMenuItem
                key="youtube"
                onClick={() => {
                  // Do nothing - disabled item
                }}
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                <IconWrapper height="16px" width="16px">
                  {/* @ts-expect-error react-icons type mismatch */}
                  <LuYoutube />
                </IconWrapper>
                <span>
                  <T keyName="Youtube link" defaultValue="Youtube link" />
                </span>
              </StyledMenuItem>
            </Tooltip>
          </Menu>
        }
        trigger={['click']}
      >
        <AddResourcesDropdownButton type="white">
          <Space>
            <IconWrapper height="15px" width="15px">
              {/* @ts-expect-error react-icons type mismatch */}
              <LuPaperclip style={{ strokeWidth: '1.75px' }} />
            </IconWrapper>
            {isDesktop ? (
              <T keyName="ImportMaterials.button.label" defaultValue="Add Materials" />
            ) : null}
          </Space>
          <IconWrapper style={{ transform: 'rotate(-90deg)' }} height="16px" width="16px">
            <ArrowLeftSvg />
          </IconWrapper>
        </AddResourcesDropdownButton>
      </Dropdown>

      <UseMyTextModal
        tenantId={tenantId}
        isVisible={showPasteModal}
        onCancel={() => setShowPasteModal(false)}
        onFileUploadSuccess={onFileUpload}
      />
    </>
  );
}

const StyledMenuItem = styled(Menu.Item)`
  & > span {
    display: grid !important;
    grid-template-columns: 24px 1fr;
    gap: 12px;
    align-items: center;

    .ant-dropdown-menu-item-icon {
      margin-right: 0 !important;
    }
  }
`;

const DropdownButton = styled(Button)`
  border: 1px solid ${theme.colors.greyLight};
  box-shadow: none;
  padding: 10px 15px;
  border-radius: 20px;
  background: none;
  height: 30px;
  display: flex;
  align-items: space-between;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s ease;
  font-weight: 400;

  span {
    font-weight: 400;
  }
  font-size: 16px;
  color: ${theme.colors.black};
  svg {
    margin: 0px;
    height: 18px;
    width: 18px;
  }
`;

const AddResourcesDropdownButton = styled(DropdownButton)`
  border: 1px solid transparent;
  padding: 10px;
  height: 35px;
  border-radius: 10px;

  &:hover,
  &:focus {
    background-color: ${theme.colors.greyLight};
    border: 1px solid ${theme.colors.grey};
  }

  @media (max-width: 768px) {
    height: 32px;
    padding: 8px;
    font-size: 14px;
    color: ${theme.colors.greyDark};

    &:hover,
    &:focus {
      background-color: ${theme.colors.grey};
    }

    span {
      font-size: 14px;
      color: ${theme.colors.greyDark};
    }

    svg {
      color: ${theme.colors.greyDark};
    }
  }
`;
